/**
 * Tax arithmetic — the ONE place both SPAs compute a tax, a gross total or a
 * base from a total.
 *
 * Rates are percentages (`21` = 21 %). A rate of `null`/`undefined` means the
 * workshop never configured a tax, which is NOT the same as `0`: a chosen 0 %
 * is a decision («no cobro impuestos»), a missing rate is a gap the UI warns
 * about. Both compute a tax of 0, so the flag `configured` is what tells them
 * apart.
 *
 * Money is rounded to cents only where a figure is SHOWN or stored
 * (`round2`); intermediate products are never rounded twice.
 *
 * No imports on purpose: the tenant's Node test runner
 * (`tenant/frontend/hooks/taxMath.test.ts`) loads this file by path, strips
 * the types and resolves no extension-less imports.
 */

/** A tax rate as the server sends it: a percentage, or null = not configured. */
export type TaxRate = number | null | undefined;

/** Whether a rate was configured (0 counts: it is a decision). */
export function isTaxConfigured(rate: TaxRate): rate is number {
    return typeof rate === 'number' && Number.isFinite(rate);
}

/** A rate as typed in a form, read by `parseTaxRate`. */
export interface ParsedTaxRate {
    /** The percentage, or `null` when the field is empty (or invalid). */
    value: number | null;
    /** `false` when the text is not a number in 0..100 — the servers answer 422. */
    valid: boolean;
}

/**
 * A rate field's text → its value. Empty ⇒ `null` («sin configurar», valid);
 * a comma is a decimal point; anything that is not a number in 0..100 is
 * invalid. The ONE reading of a typed rate: the workshop's Información fiscal
 * and the Admin's Taller → General write the same column, and must agree on
 * what «no configurado» and «inválido» mean.
 */
export function parseTaxRate(raw: string): ParsedTaxRate {
    const s = raw.trim().replace(',', '.');
    if (s === '') return { value: null, valid: true };
    const n = Number(s);
    if (!Number.isFinite(n) || n < 0 || n > 100) return { value: null, valid: false };
    return { value: n, valid: true };
}

/**
 * Round to cents, half away from zero. `toPrecision(15)` absorbs the binary
 * noise first, so `1.005` rounds to `1.01` (a bare `Math.round(1.005 * 100)`
 * gives `1.00`, because `1.005 * 100` is `100.49999999999999`).
 */
export function round2(n: number): number {
    if (!Number.isFinite(n)) return 0;
    const scaled = Number((Math.abs(n) * 100).toPrecision(15));
    const out = (Math.sign(n) * Math.round(scaled)) / 100;
    // Never hand out a negative zero: it prints as "-0,00 €".
    return out === 0 ? 0 : out;
}

/** Tax on a net amount, in cents. Unconfigured rate ⇒ 0. */
export function taxAmount(net: number, rate: TaxRate): number {
    if (!isTaxConfigured(rate)) return 0;
    return round2((net * rate) / 100);
}

export interface GrossTotals {
    /** Taxable base: price + shipping (shipping is taxed like the goods). */
    base: number;
    tax: number;
    gross: number;
    /** False when the rate is null/undefined — `tax` is then 0 and `gross` = `base`. */
    configured: boolean;
}

/**
 * The totals of a quote or an order. Shipping goes INSIDE the taxable base
 * (a delivery charged to the customer follows the tax of what it delivers).
 */
export function grossTotals(price: number, shipping: number | null | undefined, rate: TaxRate): GrossTotals {
    const base = round2((price || 0) + (shipping || 0));
    const configured = isTaxConfigured(rate);
    const tax = configured ? taxAmount(base, rate) : 0;
    return { base, tax, gross: round2(base + tax), configured };
}

/** The net amount inside a gross one. Unconfigured rate ⇒ the gross itself. */
export function netFromGross(gross: number, rate: TaxRate): number {
    if (!isTaxConfigured(rate)) return round2(gross);
    return round2(gross / (1 + rate / 100));
}

/** An expense typed as its base: the tax and the total that go with it. */
export function expenseFromBase(base: number, rate: TaxRate): { tax: number; total: number } {
    const tax = taxAmount(base, rate);
    return { tax, total: round2(base + tax) };
}

/**
 * An expense typed as its total (what the invoice says was paid): the base and
 * the tax inside it. The tax is the DIFFERENCE, not a second rounding, so
 * base + tax always equals the total to the cent.
 *
 * The total is taken in cents first: splitting `1.005` at 0 % would otherwise
 * give a base of `1.01` and a tax of `-0.01` (the server does the same,
 * `expense_tax.normalise`).
 */
export function expenseFromTotal(total: number, rate: TaxRate): { base: number; tax: number } {
    const cents = round2(total);
    const base = netFromGross(cents, rate);
    return { base, tax: round2(cents - base) };
}

/**
 * How far base + tax is from the total, when it is more than a cent off —
 * the signed difference `(base + tax) − total` — or `null` when they agree.
 * Any missing figure ⇒ `null` (nothing to compare).
 */
export function expenseMismatch(
    base: number | null | undefined,
    tax: number | null | undefined,
    total: number | null | undefined,
): number | null {
    if (![base, tax, total].every((v) => typeof v === 'number' && Number.isFinite(v))) return null;
    const diff = round2((base as number) + (tax as number) - (total as number));
    return Math.abs(diff) > 0.01 ? diff : null;
}

/**
 * A money field's text → its number, or `null` when empty or not a number.
 * A comma is a decimal point (a `type="number"` control already sends a dot,
 * but a draft or a pasted value may not).
 */
export function parseAmount(raw: string | number | null | undefined): number | null {
    if (raw === null || raw === undefined) return null;
    if (typeof raw === 'number') return Number.isFinite(raw) ? raw : null;
    const s = raw.trim().replace(',', '.');
    if (s === '') return null;
    const n = Number(s);
    return Number.isFinite(n) ? n : null;
}

/** A number as a money field holds it: rounded to cents, no trailing zeros. */
export function amountField(n: number | null | undefined): string {
    return typeof n === 'number' && Number.isFinite(n) ? String(round2(n)) : '';
}

/** The four money fields of an expense, as the form holds them (raw text). */
export interface ExpenseFigures {
    base: string;
    rate: string;
    tax: string;
    total: string;
}

export type ExpenseFigure = keyof ExpenseFigures;

/**
 * The money figure the others are derived FROM: the last of base and total
 * the user actually typed. A figure the form derived never becomes the
 * anchor — that is what keeps a rate typed after a total (`2`, then `21`)
 * splitting the TOTAL on the second keystroke, instead of growing a new total
 * from the base the first keystroke derived.
 */
export type ExpenseAnchor = 'base' | 'total';

/**
 * The anchor of figures the form did not see being typed (a stored expense, a
 * draft). A split WITH a rate follows its total — the migration derived every
 * historic split from the total at 21 %, and a typed total splits the same
 * way — so correcting that rate keeps what was paid. A base and a tax without
 * a rate were entered one by one (an invoice with several rates): the base
 * stays put. With no base there is only the total.
 */
export function defaultExpenseAnchor(f: ExpenseFigures): ExpenseAnchor {
    return parseAmount(f.base) !== null && parseTaxRate(f.rate).value === null ? 'base' : 'total';
}

export interface ExpenseRecalc {
    next: ExpenseFigures;
    /** The OTHER fields this edit rewrote (never the edited one). */
    derived: ExpenseFigure[];
    /** The anchor after this edit (typing a base or a total makes it the anchor). */
    anchor: ExpenseAnchor;
}

/**
 * One keystroke in the expense form → the four figures after it.
 *
 * - base  → becomes the anchor. With a rate: tax and total from it; without
 *   one, total = base + tax when there is a tax (nothing is invented otherwise);
 * - rate  → re-derives from the anchor: base → tax and total; total → base and
 *   tax inside it (from the other figure when the anchor is empty);
 * - tax   → anchor base: total = base + tax; anchor total: base = total − tax;
 * - total → becomes the anchor. With a rate: base and tax inside it. Without
 *   one, a base that was derived from the total follows it (total − tax, or the
 *   total itself when it was only mirroring it); a derived base nothing can
 *   re-derive is cleared rather than left to invent a tax on save. A base the
 *   user typed never moves.
 *
 * The rate is read with `parseTaxRate`: an invalid one derives nothing (the
 * form flags it). Whatever does not add up afterwards is what
 * `expenseMismatch` reports — this never "fixes" a figure the user typed.
 */
export function recalcExpense(
    prev: ExpenseFigures,
    field: ExpenseFigure,
    value: string,
    anchor: ExpenseAnchor,
): ExpenseRecalc {
    const next: ExpenseFigures = { ...prev, [field]: value };
    const derived: ExpenseFigure[] = [];
    const set = (k: ExpenseFigure, n: number) => {
        next[k] = amountField(n);
        derived.push(k);
    };
    const clear = (k: ExpenseFigure) => {
        next[k] = '';
        derived.push(k);
    };
    const base = parseAmount(next.base);
    const tax = parseAmount(next.tax);
    const total = parseAmount(next.total);
    const rate = parseTaxRate(next.rate).value;
    const fromBase = (b: number, r: number) => {
        const out = expenseFromBase(b, r);
        set('tax', out.tax);
        set('total', out.total);
    };
    const fromTotal = (t: number, r: number) => {
        const out = expenseFromTotal(t, r);
        set('base', out.base);
        set('tax', out.tax);
    };

    switch (field) {
        case 'base':
            if (base === null) break;
            if (rate !== null) fromBase(base, rate);
            else if (tax !== null) set('total', base + tax);
            break;
        case 'rate': {
            if (rate === null) break;
            const useTotal = anchor === 'total' ? total !== null : base === null && total !== null;
            if (useTotal) fromTotal(total as number, rate);
            else if (base !== null) fromBase(base, rate);
            break;
        }
        case 'tax': {
            if (tax === null) break;
            const useTotal = anchor === 'total' ? total !== null : base === null && total !== null;
            if (useTotal) set('base', (total as number) - tax);
            else if (base !== null) set('total', base + tax);
            break;
        }
        case 'total':
            if (total === null) break;
            if (rate !== null) {
                fromTotal(total, rate);
            } else if (anchor === 'total' && base !== null) {
                if (tax !== null) set('base', total - tax);
                else if (parseAmount(prev.base) === parseAmount(prev.total)) set('base', total);
                else clear('base');
            }
            break;
    }
    const nextAnchor: ExpenseAnchor = field === 'base' || field === 'total' ? field : anchor;
    return { next, derived, anchor: nextAnchor };
}

/** What the server will store for these figures (`expense_tax.normalise`). */
export interface DerivedExpense {
    base: number | null;
    tax: number | null;
    total: number | null;
    /**
     * `(base + tax) − total` when the server would refuse the figures — they
     * disagree by more than a cent, or completing one would make it negative
     * (a base above the total, a tax above the total) — else `null`.
     */
    mismatch: number | null;
}

/**
 * The figures as the server completes them before checking, so the form warns
 * about every save the server will refuse — not only when all three money
 * fields are filled in. The mirror of `expense_tax.normalise`:
 *
 * - no base and no tax: with a rate, both come out of the total;
 * - no tax: with a rate, from the base; else total − base (negative ⇒ refused);
 * - no base: total − tax (negative ⇒ refused); with no total, from the rate;
 * - no total: base + tax.
 *
 * Derivations work from the total in cents, like the server. The warning uses
 * `expenseMismatch`'s one-cent threshold, stricter than the server's two.
 */
export function deriveExpense(f: ExpenseFigures): DerivedExpense {
    const total = parseAmount(f.total);
    const rawBase = parseAmount(f.base);
    const rawTax = parseAmount(f.tax);
    let base = rawBase === null ? null : round2(rawBase);
    let tax = rawTax === null ? null : round2(rawTax);
    const rate = parseTaxRate(f.rate).value;
    const cents = total === null ? null : round2(total);
    const refused = (b: number, t: number): DerivedExpense => ({
        base,
        tax,
        total,
        mismatch: round2(b + t - (total as number)),
    });

    if (base === null && tax === null) {
        if (rate !== null && cents !== null) ({ base, tax } = expenseFromTotal(cents, rate));
    } else if (tax === null) {
        const b = base as number;
        if (rate !== null) tax = taxAmount(b, rate);
        else if (cents !== null) {
            tax = round2(cents - b);
            if (tax < 0) return refused(b, 0);
        }
    } else if (base === null) {
        if (cents !== null) {
            base = round2(cents - tax);
            if (base < 0) return refused(0, tax);
        } else if (rate) {
            base = round2((tax * 100) / rate);
        }
    }
    const outTotal = total === null && base !== null && tax !== null ? round2(base + tax) : total;
    return { base, tax, total: outTotal, mismatch: expenseMismatch(base, tax, outTotal) };
}

/** The money figures an invoice analysis can return (any may be missing). */
export interface AnalysedExpenseFigures {
    base_amount?: number | string | null;
    tax_rate?: number | string | null;
    tax_amount?: number | string | null;
    /** What the invoice says was paid. */
    total?: number | string | null;
    /** Legacy name of the total (older analysers send only this). */
    amount?: number | string | null;
}

/**
 * An analysis result → the four figures of the form, as ONE block (whatever
 * the form held before is replaced, so the block always comes from the same
 * invoice). `null` when the analysis read no amount at all.
 *
 * Missing figures are completed from the ones read — base + tax → total,
 * total + rate → base and tax, and so on. A total alone stays alone: the base
 * is left EMPTY, so a rate typed afterwards splits that total instead of
 * growing a new one from a base that merely copied it (and saving without a
 * rate stores the total with no split, which Financiero counts whole). A
 * completion that would come out negative (a base read above the total) is
 * not made: the figures stay as read and the server's 422 names the problem.
 * A rate outside 0..100 is dropped rather than propagated.
 */
export function expenseFiguresFromAnalysis(a: AnalysedExpenseFigures): ExpenseFigures | null {
    let base = parseAmount(a.base_amount ?? null);
    let tax = parseAmount(a.tax_amount ?? null);
    let total = parseAmount(a.total ?? null) ?? parseAmount(a.amount ?? null);
    const rawRate = parseAmount(a.tax_rate ?? null);
    const rate = rawRate !== null && rawRate >= 0 && rawRate <= 100 ? rawRate : null;
    if (base === null && tax === null && total === null) return null;
    const nonNegative = (n: number) => (n >= 0 ? n : null);

    if (base === null && total !== null) {
        if (rate !== null) {
            const r = expenseFromTotal(total, rate);
            base = r.base;
            if (tax === null) tax = r.tax;
        } else if (tax !== null) {
            base = nonNegative(round2(total - tax));
        }
    }
    if (base !== null && tax === null) {
        if (rate !== null) tax = taxAmount(base, rate);
        else if (total !== null && total !== base) tax = nonNegative(round2(total - base));
    }
    if (total === null && base !== null) {
        total = round2(base + (tax ?? 0));
    }
    return {
        base: amountField(base),
        rate: rate === null ? '' : String(rate),
        tax: amountField(tax),
        total: amountField(total),
    };
}
